import { useCallback, useEffect, useMemo, useRef } from "react";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/app/providers/useAuth";
import { useRecentFiles } from "../hooks/useRecentFiles";
import { getGreeting } from "../utils/feedUtils";
import { FeedCard } from "./FeedCard";
import { LoadingSkeleton, EmptyState, FeedEnd, LoadingMore } from "./FeedStates";

export function HomeOverview() {
  const { displayName } = useAuth();
  const { buckets, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useRecentFiles();

  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: "400px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const firstName = useMemo(() => {
    if (!displayName || displayName === "User") return null;
    return displayName.split(" ")[0];
  }, [displayName]);

  return (
    <section className="flex-1 min-h-0 overflow-y-auto space-y-8 pt-6 pl-4 pr-4 pb-8">
      <header className="space-y-1">
        <h1 className="font-display text-3xl text-ink-900 md:text-4xl">
          {getGreeting()}
          {firstName ? `, ${firstName}` : ""}
        </h1>
      </header>

      <div className="mx-auto max-w-3xl space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-imperial-500" />
          <h2 className="text-base font-semibold text-ink-800">
            See what's new
          </h2>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : buckets.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-6">
            {buckets.map(({ label, items }) => (
              <div key={label} className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wider text-ink-400 pl-1">
                  {label}
                </p>
                <div className="space-y-2">
                  {items.map((entry) => (
                    <FeedCard key={entry.file.fileId} entry={entry} />
                  ))}
                </div>
              </div>
            ))}

            <div ref={sentinelRef} aria-hidden="true" />

            {isFetchingNextPage && <LoadingMore />}
            {!hasNextPage && !isFetchingNextPage && <FeedEnd />}
          </div>
        )}
      </div>
    </section>
  );
}
